import * as React from "react"
import { DayPicker, type DropdownProps } from "react-day-picker"
import "react-day-picker/dist/style.css"
import { ChevronDown } from "lucide-react"

import { cn } from "@/lib/utils"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

export type CalendarProps = React.ComponentProps<typeof DayPicker>

// Custom dropdown component for month/year selectors
// This ensures consistent styling across all browsers (Windows, Mac, etc.)
function CustomDropdown({
  options,
  value,
  onChange,
  "aria-label": ariaLabel,
}: DropdownProps) {
  // Sort options in descending order for year dropdown, ascending for month dropdown
  const sortedOptions = React.useMemo(() => {
    if (!options) return []
    // Check if this is a year dropdown (values are years, typically 1900-2025)
    const isYearDropdown = options.some(opt => opt.value >= 1900 && opt.value <= 2100)
    if (isYearDropdown) {
      // Sort years in descending order (2025, 2024, ...)
      return [...options].sort((a, b) => b.value - a.value)
    }
    // For month dropdown, keep ascending order (1, 2, 3, ...)
    return options
  }, [options])
  
  const selectedOption = sortedOptions.find((option) => option.value === value)
  const selectRef = React.useRef<HTMLSelectElement>(null)

  // Handle selection by creating a synthetic event that mimics native select behavior
  const handleSelect = (newValue: number) => {
    if (selectRef.current && onChange) {
      selectRef.current.value = String(newValue)
      // Create a synthetic event to pass to onChange
      const syntheticEvent = {
        target: selectRef.current,
        currentTarget: selectRef.current,
      } as React.ChangeEvent<HTMLSelectElement>
      onChange(syntheticEvent)
    }
  }

  return (
    <div className="relative inline-flex">
      {/* Hidden native select for form compatibility and accessibility */}
      <select
        ref={selectRef}
        value={value}
        onChange={onChange}
        aria-label={ariaLabel}
        className="sr-only"
        tabIndex={-1}
      >
        {sortedOptions.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={cn(
              "flex items-center gap-1 px-2 py-1 text-sm font-medium rounded-md",
              "border border-border bg-transparent",
              "hover:border-primary-500 hover:bg-accent/50",
              "dark:border-grey-700 dark:hover:border-primary-400 dark:hover:bg-blue-800",
              "focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-0",
              "transition-colors duration-150"
            )}
            aria-label={ariaLabel}
          >
            <span className="text-foreground">{selectedOption?.label}</span>
            <ChevronDown className="h-3 w-3 text-foreground opacity-70" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className={cn(
            "max-h-[280px] overflow-y-auto min-w-[100px]",
            "bg-white dark:bg-blue-900",
            "border border-grey-300 dark:border-grey-700",
            "shadow-lg"
          )}
          align="start"
        >
          {sortedOptions.map((option) => (
            <DropdownMenuItem
              key={option.value}
              onClick={() => handleSelect(option.value)}
              className={cn(
                "cursor-pointer text-sm",
                "bg-white dark:bg-blue-900",
                "hover:bg-grey-200 dark:hover:bg-blue-700",
                "focus:bg-grey-200 dark:focus:bg-blue-700",
                option.value === value && "bg-grey-200 dark:bg-blue-700 font-medium"
              )}
            >
              {option.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

function Calendar({
  className,
  showOutsideDays = true,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("rdp", className)}
      components={{
        Dropdown: CustomDropdown,
      }}
      {...props}
    />
  )
}
Calendar.displayName = "Calendar"

export { Calendar }
